/**
 * useRealtimeNotifications — subscribe to the backend WebSocket at
 *   /api/ws/notifications?token=<jwt>
 *
 * Guarantees:
 *   - Auto-reconnect with exponential backoff (1s → 30s cap)
 *   - 45s client keepalive ping; handles server "pong"/"keepalive"
 *   - Silently no-ops if no JWT is present (e.g. on /login)
 *   - `onEvent(event)` is invoked for every server-pushed message
 *   - Existing polling remains intact — this is additive only
 *
 * Usage:
 *   useRealtimeNotifications((evt) => {
 *     if (evt.kind === 'mail') refetchEmails();
 *   });
 */
import { useEffect, useRef, useState } from 'react';
import { API } from '@/App';

export function useRealtimeNotifications(onEvent) {
  const [isConnected, setIsConnected] = useState(false);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // Not logged in — skip

    // Derive ws://... or wss://... from the REST base (API = https://.../api)
    const wsBase = API.replace(/^http/i, 'ws');
    const url = `${wsBase}/ws/notifications?token=${encodeURIComponent(token)}`;

    let ws = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let backoffMs = 1000;
    let cancelled = false;

    const clearTimers = () => {
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      const delay = Math.min(backoffMs, 30000);
      reconnectTimer = setTimeout(() => {
        backoffMs = Math.min(backoffMs * 2, 30000);
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        if (cancelled) { try { ws.close(); } catch (_) {} return; }
        setIsConnected(true);
        backoffMs = 1000; // reset backoff on successful connect
        // Heartbeat every 45s
        pingTimer = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send('ping'); } catch (_) {}
          }
        }, 45000);
      };

      ws.onmessage = (msg) => {
        let payload;
        try {
          payload = JSON.parse(msg.data);
        } catch (_) {
          return;
        }
        // Keepalive / pong — ignore
        if (payload?.type === 'pong' || payload?.type === 'keepalive') return;
        try {
          handlerRef.current && handlerRef.current(payload);
        } catch (e) {
          // Never let a consumer error kill the socket
          // eslint-disable-next-line no-console
          console.error('[WS] consumer error:', e);
        }
      };

      ws.onerror = () => {
        // onclose will follow — handle reconnect there.
      };

      ws.onclose = () => {
        setIsConnected(false);
        clearTimers();
        ws = null;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimers();
      if (ws) {
        try { ws.close(); } catch (_) {}
      }
      setIsConnected(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { isConnected };
}

export default useRealtimeNotifications;
